'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { Users, Search, Target, ArrowRight, CheckCircle } from 'lucide-react'
import Link from 'next/link'
import ConsultationModal from './modals/ConsultationModal'
import DynamicCompanyInfo from './DynamicCompanyInfo'

export default function RecruitmentBanner() {
  const [showConsultationModal, setShowConsultationModal] = useState(false)
  const highlights = [
    'Successful Placements Across Industries',
    'Fast Initial Response Time',
    'Dedicated Recruitment Specialists',
    'Complete Recruitment Solutions'
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-blue-900 via-slate-900 to-purple-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="inline-flex items-center px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full text-blue-300 text-sm font-medium mb-6"
              >
                <Users className="w-4 h-4 mr-2" />
                Complete Recruitment Solutions
              </motion.div>
              
              <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Need Top Talent?
                <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  All Industries Covered
                </span>
              </h2>
              
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                <DynamicCompanyInfo type="companyName">
                  {(companyInfo) => (
                    <>
                      Beyond our IT services, {companyInfo?.companyName || 'our company'} provides comprehensive recruitment across all industries. 
                      From executives to entry-level, technical to creative - we find the right talent for every role.
                    </>
                  )}
                </DynamicCompanyInfo>
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {highlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center"
                >
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{highlight}</span>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link href="/recruitment">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="group bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:shadow-blue-500/25 transition-all duration-300 font-semibold flex items-center"
                >
                  Explore Recruitment Services
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </motion.button>
              </Link>
              
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowConsultationModal(true)}
                className="border-2 border-blue-500/50 text-blue-300 px-8 py-4 rounded-xl hover:border-blue-400 hover:bg-blue-600/10 transition-all duration-300 font-semibold"
              >
                Discuss Hiring Needs
              </motion.button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Recruitment Illustration */}
            <div className="relative w-full h-96 bg-gradient-to-br from-blue-600/20 to-purple-600/20 rounded-3xl border border-blue-500/30 backdrop-blur-sm overflow-hidden">
              <svg
                className="w-full h-full"
                viewBox="0 0 400 300"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Team Network */}
                <circle cx="200" cy="150" r="25" fill="rgba(59, 130, 246, 0.8)" />
                <circle cx="120" cy="100" r="20" fill="rgba(16, 185, 129, 0.8)" />
                <circle cx="280" cy="100" r="20" fill="rgba(245, 158, 11, 0.8)" />
                <circle cx="120" cy="200" r="20" fill="rgba(239, 68, 68, 0.8)" />
                <circle cx="280" cy="200" r="20" fill="rgba(139, 92, 246, 0.8)" />
                
                {/* Connection Lines */}
                <path d="M175 150 L145 115" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2" />
                <path d="M225 150 L255 115" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2" />
                <path d="M175 150 L145 185" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2" />
                <path d="M225 150 L255 185" stroke="rgba(59, 130, 246, 0.6)" strokeWidth="2" />
                
                {/* Search Icons */}
                <circle cx="200" cy="150" r="35" fill="none" stroke="rgba(59, 130, 246, 0.4)" strokeWidth="2" strokeDasharray="5,5" />
                <circle cx="200" cy="150" r="50" fill="none" stroke="rgba(139, 92, 246, 0.3)" strokeWidth="1" strokeDasharray="3,3" />
                
                {/* Talent Pool */}
                <rect x="80" y="50" width="15" height="15" rx="3" fill="rgba(255, 255, 255, 0.7)" />
                <rect x="305" y="50" width="15" height="15" rx="3" fill="rgba(255, 255, 255, 0.7)" />
                <rect x="80" y="235" width="15" height="15" rx="3" fill="rgba(255, 255, 255, 0.7)" />
                <rect x="305" y="235" width="15" height="15" rx="3" fill="rgba(255, 255, 255, 0.7)" />
              </svg>
              
              {/* Floating Elements */}
              <motion.div
                animate={{ 
                  y: [-15, 15, -15],
                  rotate: [0, 180, 360]
                }}
                transition={{ repeat: Infinity, duration: 8 }}
                className="absolute top-8 right-8 w-8 h-8 bg-gradient-to-r from-blue-400 to-cyan-500 rounded-lg"
              />
              <motion.div
                animate={{ 
                  y: [15, -15, 15],
                  x: [-8, 8, -8]
                }}
                transition={{ repeat: Infinity, duration: 10 }}
                className="absolute bottom-8 left-8 w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full"
              />
              <motion.div
                animate={{ 
                  scale: [1, 1.3, 1],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ repeat: Infinity, duration: 6 }}
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Consultation Modal */}
      <ConsultationModal 
        isOpen={showConsultationModal}
        onClose={() => setShowConsultationModal(false)}
        service="Recruitment Services"
      />
    </section>
  )
}